import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PageTitle, useFeatures, useFetch, useSession } from '@cordata/buffet';
import { authorizedPyInstance as py } from '~/utils';
import { deburrObject } from '~/utils/api/normalizers';

import OutboundReferralForm from '~/components/OutboundReferral/Form';

const OutboundReferralAdd = () => {
  const params = useParams();
  const { current, currentAppUser } = useSession();
  const { hasAppCit } = useFeatures();

  const [, providers] = useFetch('providers');
  const [, specialties] = useFetch('provider-roles');
  const [referralReasons, setReferralReasons] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch reference data using py API
  useEffect(() => {
    const fetchAllReferenceData = async () => {
      if (!hasAppCit) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch all reference data in parallel using py API
        const [
          reasonsRes,
          prioritiesRes,
          statusesRes,
        ] = await Promise.all([
          py.get('api/data/reference/reference-reason', {
            params: {
              ref_data_type: 'outbound referral',
            },
          }),

          py.get('api/data/reference/reference-priority', {
            params: {
              'all-data': 1,
              sort: 'default-sort-order,priority-label',
            },
          }),
          py.get('api/data/reference/reference-status', {
            params: {
              ref_data_type: 'outbound referral',
            },
          }),
        ]);

        setReferralReasons((reasonsRes.data.data || []).map(deburrObject));
        setPriorities((prioritiesRes.data.data || []).map(deburrObject));
        setStatuses((statusesRes.data.data || []).map(deburrObject));
      } catch (error) {
        // Set empty arrays as fallbacks
        setReferralReasons([]);
        setPriorities([]);
        setStatuses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllReferenceData();
  }, [hasAppCit]);

  // Find appropriate reference data IDs for CIT defaults
  const citDefaults = useMemo(() => {
    if (
      !hasAppCit ||
      loading ||
      !Array.isArray(providers) ||
      !Array.isArray(specialties) ||
      !Array.isArray(referralReasons) ||
      !Array.isArray(priorities) ||
      !Array.isArray(statuses)
    ) {
      return {};
    }

    const providersOptions = providers.map(deburrObject);
    const specialtiesOptions = specialties.map(deburrObject);

    // ✅ Mental health specialty defaults (case-insensitive search)
    const mentalHealthSpecialty = specialtiesOptions.find(s => {
      const nameLower = s.attributes?.name?.toLowerCase();
      return nameLower?.includes('mental health') ||
        nameLower?.includes('behavioral health') ||
        nameLower?.includes('crisis');
    });

    // ✅ From provider defaults to current user (provider record) - Fix SC-51684 Issue #1
    // Try to get provider info from session first
    let currentUserProvider = current?.attributes?.provider ||
      current?.attributes?.currentProvider ||
      currentAppUser?.attributes?.provider;

    // If no session provider, match by user identification in existing providers list
    if (!currentUserProvider && providersOptions.length > 0) {
      currentUserProvider = providersOptions.find((p) => {
        // Only check providers that have a user relationship
        if (!p.relationships?.user) {
          return false;
        }
        // Check if the linked user matches the current user
        const linkedUser = p.relationships.user;
        return linkedUser.id === current?.attributes?.userUid ||
          linkedUser.attributes?.userUid === current?.attributes?.userUid ||
          linkedUser.attributes?.userName === current?.attributes?.userName;
      });
    }

    // ✅ Priority defaults to "Next available" (string)
    const nextAvailablePriority = priorities.find((p) => {
      const priorityName = p.attributes?.priorityName;
      return priorityName === 'Next available' ||
        priorityName?.toLowerCase().includes('next available');
    });

    // ✅ Reason defaults to "Consultation" (string)
    const consultationReason = referralReasons.find((r) => {
      const reasonName = r.attributes?.reasonName;
      return reasonName === 'Consultation' ||
        reasonName?.toLowerCase().includes('consult');
    });

    // ✅ Status defaults to "Sent" (use id; your values.status looks like an object)
    const sentStatus = statuses.find((s) => {
      const statusName = s.attributes?.statusName;
      return statusName === 'Sent' ||
        statusName?.toLowerCase().includes('sent');
    });

    return {
      // ✅ event date defaults to today (editable)
      eventDate: new Date(),

      // ✅ refer-to defaults (editable)
      // ✅ defaults (editable)
      ...(mentalHealthSpecialty && { specialty: mentalHealthSpecialty }),
      ...(currentUserProvider && { fromProvider: currentUserProvider }),
      ...(nextAvailablePriority && { priority: nextAvailablePriority }),
      ...(consultationReason && { reason: consultationReason }),

      // ✅ defaults (not editable later in Fields)
      ...(sentStatus && { status: sentStatus }),
    };
  }, [
    hasAppCit,
    loading,
    providers,
    specialties,
    referralReasons,
    priorities,
    statuses,
    current,
    currentAppUser, // Session data with extended user information
  ]);

  return (
    <>
      <PageTitle>Add Outbound Referral</PageTitle>

      {loading ? (
        <div>Loading reference data...</div>
      ) : (
        <OutboundReferralForm
          data={{
            patientUid: params.patient_id,
            ...citDefaults,
          }}
          isNew
        />
      )}
    </>
  );
};

export default OutboundReferralAdd;
